import React, { useCallback, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useExecutionContext } from '@/features/executions/execution-context';
import { LineItemGraph } from '@/features/executions/line-item-graph';
import { MediaPlansTableContainer } from '@/features/media-plans/media-plans-table';
import { Granularity } from '@/components/charts/chart-type-selector';
import { ExecutionLineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { trpc } from '@/utils/trpc-client';
import {
  LineItemContextProvider,
  useLineItemContext
} from '@/features/line-items/line-items-context';
import { lineItemTitle, PageTitle } from '@/features/shared/page-title';
import {
  ExpandableSidebarSection,
  PageContentSection,
  PageSection,
  TopSection
} from '@/components/layout/layout';
import { LineItemBreadcrumbsV2 } from '@/components/layout/breadcrumbs';
import { faChartLine, faClockRotateLeft, faGears } from '@fortawesome/pro-light-svg-icons';
import { DetailsRail } from '@/components/display/details-rail';
import { PacingSchedule } from '@/features/line-items/pacing-schedule';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import { MediaPlanPage } from '@/features/media-plans/media-plan-page';
import { useCaptureLineItemView } from '@/utils/posthog/analytics';

import { NoMediaPlansPage } from '@/features/line-items/no-media-plans-page';
import { NavItem } from '@/components/layout/nav-item';
import { useFlagEnabled } from '@/utils/feature-flags';
import { CampaignV2 } from 'shared/src/api/campaigns-v2';
import { getDefaultTimeFrame } from '@/features/line-items/time-frame-utils';
import type { TimeFrameMode } from '@/features/line-items/time-frame-utils';
import { ISODateRange } from 'shared/src/date-string-utils/types';
import { TimeFrameSelector } from '@/features/line-items/time-frame-selector';
import { LineItemHeroMetrics } from '@/features/line-items/line-item-hero-metrics';
import { LineItemDailyKpis } from '@/features/line-items/line-item-daily-kpis';
import { ActivityFeed } from '@/features/activity/ActivityFeed';
import { ActivityFilterBar } from '@/features/activity/ActivityFilterBar';
import type { ActivityType, ActivityFeedFilters } from 'shared/src/api/activity-feed-api-types';

export function ExecutionLineItemPage() {
  const { lineItemId } = useParams();
  const { lineItems } = useExecutionContext();
  const { campaign } = useCampaignContext();
  const lineItem = findLineItem(lineItems, lineItemId);
  return lineItem ? (
    <LineItemPageInner lineItem={lineItem} />
  ) : (
    <Navigate to={`/campaigns/${campaign.campaign_id}/proposal/lineitems/${lineItemId}`} replace />
  );
}

function LineItemPageInner({ lineItem }: { lineItem: ExecutionLineItemTrpcResponse }) {
  const { campaign } = useCampaignContext();
  useCaptureLineItemView(lineItem);
  const [granularity, setGranularity] = useState<Granularity>('day');
  const activityEnabled = useFlagEnabled('activity-log-v1');
  const strategy_id = lineItem.strategy_id;
  const line_item_id = lineItem.id;
  const lineItemMetrics = trpc.lineItemMetrics.useQuery({ strategy_id, line_item_id });
  const mediaPlans = trpc.listMediaPlans.useQuery({ strategy_id, line_item_id });
  const mediaPlansMetrics = trpc.lineItemMediaPlansMetrics.useQuery({ strategy_id, line_item_id });
  const graphMetrics = trpc.lineItemGraphMetrics.useQuery({
    strategy_id,
    line_item_id,
    granularity,
    start_date: lineItem.dates.from,
    end_date: lineItem.dates.to
  });

  return (
    <LineItemContextProvider
      lineItem={lineItem}
      graphMetrics={graphMetrics}
      mediaPlans={mediaPlans}
      mediaPlansMetrics={mediaPlansMetrics}
      lineItemMetrics={lineItemMetrics}>
      <Routes>
        <Route
          index
          element={
            <div className="flex w-full flex-col">
              <PageTitle title={lineItemTitle(lineItem.name)} />
              <TopSection>
                <LineItemBreadcrumbsV2 campaign={campaign} name={lineItem.name} />
                <div className="flex-1" />
                <div className="flex items-end justify-between">
                  <div className="flex items-end">
                    <NavItem to="." label="Overview" end />
                    {activityEnabled ? (
                      <NavItem icon={faClockRotateLeft} to="activity" label="Activity" />
                    ) : null}
                  </div>
                </div>
              </TopSection>
              <PageSection>
                <PageContentSection noPaddingTop>
                  <MainLineItemPage
                    lineItem={lineItem}
                    granularity={granularity}
                    setGranularity={setGranularity}
                  />
                </PageContentSection>
                <Sidebar lineItem={lineItem} />
              </PageSection>
            </div>
          }
        />
        <Route
          path="activity"
          element={<LineItemActivity lineItem={lineItem} campaign={campaign} />}
        />
        <Route
          path="mediaplans/:mediaPlanId"
          element={<MediaPlanPage granularity={granularity} setGranularity={setGranularity} />}
        />
      </Routes>
    </LineItemContextProvider>
  );
}

type MainPageProps = {
  granularity: Granularity;
  setGranularity: (granularity: Granularity) => void;
  lineItem: ExecutionLineItemTrpcResponse;
};

function MainLineItemPage({ granularity, setGranularity, lineItem }: MainPageProps) {
  const { mediaPlans } = useLineItemContext();
  const [timeFrameDefaults] = useState(() => getDefaultTimeFrame(lineItem));
  const [dateRange, setDateRange] = useState<ISODateRange>(() => timeFrameDefaults.dateRange);
  const [timeFrameMode, setTimeFrameMode] = useState<TimeFrameMode>(
    () => timeFrameDefaults.timeFrameMode
  );

  if (mediaPlans.length === 0) {
    return <NoMediaPlansPage />;
  }

  return (
    <>
      <TimeFrameSelector
        dateRange={dateRange}
        setDateRange={setDateRange}
        timeFrameMode={timeFrameMode}
        setTimeFrameMode={setTimeFrameMode}
      />
      <LineItemHeroMetrics dateRange={dateRange} />
      <LineItemDailyKpis timeFrameMode={timeFrameMode} dateRange={dateRange} />
      <LineItemGraph
        granularity={granularity}
        setGranularity={setGranularity}
        dateRange={dateRange}
      />
      <MediaPlansTableContainer lineItem={lineItem} dateRange={dateRange} />
    </>
  );
}

function Sidebar({ lineItem }: { lineItem: ExecutionLineItemTrpcResponse }) {
  const { execution } = useExecutionContext();

  return (
    <ExpandableSidebarSection
      sections={[
        {
          name: 'execution',
          header: 'Execution Instructions',
          icon: faGears,
          render: (setExpanded: (expanded: string) => void) => (
            <DetailsRail strategy={execution} lineItem={lineItem} setExpanded={setExpanded} />
          )
        },
        ...(lineItem && lineItem.pacing_type === 'custom'
          ? [
              {
                name: 'pacing',
                header: 'Pacing Schedule',
                icon: faChartLine,
                render: () => <PacingSchedule strategy={execution} lineItem={lineItem} />
              }
            ]
          : [])
      ]}
    />
  );
}

function findLineItem(lineItems: ExecutionLineItemTrpcResponse[], lineItemId: string | undefined) {
  return lineItems.find(lineItem => lineItem.id === lineItemId);
}

const LINE_ITEM_ACTIVITY_TYPES: ActivityType[] = ['line_item_change', 'platform_entity_change'];

type LineItemActivityProps = {
  lineItem: ExecutionLineItemTrpcResponse;
  campaign: CampaignV2;
};

function LineItemActivity({ lineItem, campaign }: LineItemActivityProps) {
  const activityEnabled = useFlagEnabled('activity-log-v1');
  const defaultFilters = useMemo<ActivityFeedFilters>(
    () => ({ lineItemId: lineItem.id }),
    [lineItem.id]
  );
  const [filters, setFilters] = useState<ActivityFeedFilters>(defaultFilters);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleClearFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  return (
    <div className="flex w-full flex-col">
      <PageTitle title={`${lineItemTitle(lineItem.name)} - Activity`} />
      <TopSection>
        <LineItemBreadcrumbsV2 campaign={campaign} name={lineItem.name} />
        <div className="flex-1" />
        <div className="flex items-end justify-between">
          <div className="flex items-end">
            <NavItem to=".." label="Overview" end />
            {activityEnabled ? <NavItem icon={faClockRotateLeft} to="." label="Activity" /> : null}
          </div>
        </div>
      </TopSection>
      <PageSection>
        <PageContentSection>
          <ActivityFilterBar
            availableActivityTypes={LINE_ITEM_ACTIVITY_TYPES}
            filters={filters}
            onFilterChange={setFilters}
            sortDirection={sortDirection}
            onSortDirectionChange={setSortDirection}
            onClearFilters={handleClearFilters}
          />
          <ActivityFeed filters={filters} sortDirection={sortDirection} showCampaignName={false} />
        </PageContentSection>
      </PageSection>
    </div>
  );
}
